import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { store } from './lib/redux/store';
import ProductExplorer, {
  ProductExplorerProps,
} from './components/ProductExplorer';
import { IntlProvider } from 'react-intl';
import { RenderFn } from 'src/types/widgets';
import normalizeWidgetInput from 'src/common/normalizeWidgetInput';
import { LinkType } from 'src/common/explorer/lib/types';
import widgetDefinition from './ProductExplorer.widget';
import { CarbonThemes } from 'src/types/carbon';
import { useProductExplorerDispatch } from './lib/redux/hooks';
import { setTheme } from './lib/redux/slices/setThemeSlice';
import { setLocaleCode } from 'src/common/explorer/lib/redux/slices/localeCodeSlice';
import 'carbon-components/css/carbon-components.min.css';

// Add a wrapping component, so we can use hooks to dispatch the theme
// provided by the user input.
const ProductExplorerApp = ({
  linkType,
  palette,
  localeCode,
}: ProductExplorerProps & { palette: CarbonThemes; localeCode: string }) => {
  const dispatch = useProductExplorerDispatch();
  dispatch(setTheme(palette));
  dispatch(setLocaleCode(localeCode));
  return <ProductExplorer linkType={linkType} />;
};
const render: RenderFn = async function (instanceId, langCode, origin, cb) {
  const { element, locale, messages, palette } = await normalizeWidgetInput(
    instanceId,
    langCode,
    widgetDefinition,
  );

  if (!element || !locale) {
    return;
  }

  // Use the locale-code that will be passed as a data attribute for link prefix
  const localeCode = element.getAttribute('data-localecode') || 'us-en';

  // Check the theme from input data. If it is not a valid theme, use the
  // palette from context.
  let theme = element.getAttribute('data-theme') || '';
  if (!Object.values<string>(CarbonThemes).includes(theme)) {
    theme = palette || CarbonThemes.GRAY_100;
  }

  const getLinkType = () => {
    const dataLinkType = element.getAttribute('data-link-type');

    return dataLinkType ? dataLinkType : LinkType.product;
  };

  ReactDOM.render(
    <React.StrictMode>
      <Provider store={store}>
        <IntlProvider locale={locale} messages={messages}>
          <ProductExplorerApp
            linkType={getLinkType()}
            palette={theme as CarbonThemes}
            localeCode={localeCode}
          />
        </IntlProvider>
      </Provider>
    </React.StrictMode>,
    element,
    () => cb(element),
  );
};

export default render;
